import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/** Desktop-only chat icon in the navbar. Visible to every logged-in user; the paid-plan
 *  gate lives inside `/chat` and the send-message endpoint. Shows a red unread badge
 *  when the user actually has unread paid conversations. */
export default function ChatNavBell() {
    const { user } = useAuth();
    const [unread, setUnread] = useState(0);
    const nav = useNavigate();
    const timer = useRef(null);

    const load = useCallback(async () => {
        try {
            const c = await api.get("/chat/unread-count");
            setUnread(c.data?.count || 0);
        } catch { /* silent — endpoint requires auth only, always safe */ }
    }, []);

    useEffect(() => {
        if (!user) { setUnread(0); return; }
        load();
        timer.current = setInterval(load, 30000);
        const refresh = () => load();
        window.addEventListener("gpm:chat:refresh", refresh);
        return () => {
            if (timer.current) clearInterval(timer.current);
            window.removeEventListener("gpm:chat:refresh", refresh);
        };
    }, [user, load]);

    if (!user) return null;

    return (
        <button
            type="button"
            onClick={() => nav("/chat")}
            data-testid="nav-chat-bell"
            aria-label="Chat hub"
            className="relative p-2 text-white/85 hover:text-gold-500 transition-colors"
        >
            <MessageSquare className="w-5 h-5" />
            {unread > 0 && (
                <span
                    data-testid="nav-chat-bell-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-navy-900"
                >
                    {unread > 99 ? "99+" : unread}
                </span>
            )}
        </button>
    );
}
